import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Button, Link } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ESPWebTools from '../ESPWebTools';

// Shown when the sensor is plugged in but never completes the HELLO handshake.
// The usual "update your sensor" wizard also relies on that handshake, so a
// sensor running firmware older than the serial command protocol (e.g. 0.0.6,
// which is BLE-only and never answers HELLO) can't be reached that way. The
// ESP Web Tools installer flashes over USB via the ESP32 ROM bootloader, which
// needs no running firmware, so it is the one recovery path that always works.

const COPY = {
  'port-busy': {
    title: 'The sensor port is in use',
    body: 'Another program or browser tab is using the sensor. Close other tabs, the Arduino IDE, or any serial monitor, then Retry.',
  },
  'open-failed': {
    title: "We couldn't open the sensor",
    body: 'On Windows you may need a one-time USB driver. Install it, replug the sensor, then Retry.',
  },
  'silent': {
    title: "Couldn't connect to your sensor",
    body: "The device didn't respond. Check your USB cable and try again.",
  },
  'transient': {
    title: 'Connection dropped',
    body: 'The sensor disconnected mid-handshake. Replug it and Retry.',
  },
};

const PANEL_SX = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 400,
  textAlign: 'center',
};

// A silent device (plugged in, never answers HELLO) almost always means firmware
// too old to speak the serial protocol. Instead of leading with Retry / "check
// your cable", we point the user straight at the USB reinstall — the one recovery
// that needs no running firmware — as the primary, highlighted action. This is
// the step people were missing: they'd Retry forever and never scroll down to it.
function FirmwarePrimaryPanel({ onRetry, manifestPath }) {
  return (
    <Box sx={PANEL_SX}>
      <ErrorOutlineIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
      <Typography variant="h5" gutterBottom>
        Can&apos;t reach your sensor
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 440 }}>
        This usually means your sensor is running older firmware that can&apos;t talk
        to this page. Reinstall over USB to fix it — no connection needed.
      </Typography>

      <Box
        sx={{
          maxWidth: 480,
          width: '100%',
          border: '2px solid',
          borderColor: 'primary.main',
          borderRadius: 2,
          p: 0.5,
        }}
      >
        <ESPWebTools
          manifestPath={manifestPath}
          title="Older firmware? Reinstall over USB"
          description={
            'Click Install below, then pick your sensor from the popup. This ' +
            'installs a fresh build directly over USB — no sensor connection ' +
            'needed. It erases the sensor and installs the latest firmware.'
          }
        />
      </Box>

      <Link component="button" variant="body2" onClick={onRetry} sx={{ mt: 1 }}>
        Just plugged it in? Retry the connection instead
      </Link>
    </Box>
  );
}

FirmwarePrimaryPanel.propTypes = {
  onRetry: PropTypes.func.isRequired,
  manifestPath: PropTypes.string.isRequired,
};

export default function ConnectFailedPanel({
  onRetry,
  onOpenWizard,
  manifestPath,
  serialSupported,
  reason = 'silent',
}) {
  const copy = COPY[reason] || COPY.silent;
  const showReinstall = serialSupported && reason !== 'port-busy' && reason !== 'transient';

  // The silent case is the old-firmware signature — give it the reinstall-first
  // layout. Every other reason keeps the Retry-first layout below.
  if (showReinstall && reason === 'silent') {
    return <FirmwarePrimaryPanel onRetry={onRetry} manifestPath={manifestPath} />;
  }

  return (
    <Box sx={PANEL_SX}>
      <ErrorOutlineIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
      <Typography variant="h5" gutterBottom>
        {copy.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400 }}>
        {copy.body}
      </Typography>
      <Button variant="contained" onClick={onRetry} sx={{ mb: 2 }}>
        Retry
      </Button>
      <Link component="button" variant="body2" onClick={onOpenWizard} sx={{ mb: 3 }}>
        Running older firmware? Update your sensor
      </Link>

      {showReinstall && (
        <Box sx={{ maxWidth: 480, width: '100%' }}>
          <ESPWebTools
            manifestPath={manifestPath}
            title="Sensor still won't connect?"
            description={
              'Sensors on very old firmware can\'t talk to this page. Reinstall the ' +
              'latest firmware directly over USB — no connection needed. This erases ' +
              'the sensor and installs a fresh build.'
            }
          />
        </Box>
      )}
    </Box>
  );
}

ConnectFailedPanel.propTypes = {
  onRetry: PropTypes.func.isRequired,
  onOpenWizard: PropTypes.func.isRequired,
  manifestPath: PropTypes.string.isRequired,
  serialSupported: PropTypes.bool.isRequired,
  reason: PropTypes.string,
};
